import { useState } from 'react';
import { api, ApiError, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { COMPLAINT_STATUSES, type Page, type SanitaryComplaint } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatusBadge } from '../../ui/components';

/** Public sanitary complaint intake -> inspection trigger — packages/modules/m25-public-health/src/routes/complaints.ts. */
export function Complaints() {
  const { persona } = useAuth();
  const isPhaStaff = (persona?.roles.includes('PH_INSPECTOR') || persona?.roles.includes('SENIOR_PHI')) ?? false;
  const [status, setStatus] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);

  const qs = new URLSearchParams({ limit: '50' });
  if (status) qs.set('status', status);
  const listQ = useApiQuery<Page<SanitaryComplaint>>(`/api/v1/m25/complaints?${qs.toString()}`);

  return (
    <div>
      <PageHeader
        title="Sanitary Complaints"
        subtitle="Public intake through to a hygiene-inspection trigger — works even against an unlicensed premises."
        actions={
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            + Log complaint
          </button>
        }
      />

      {!isPhaStaff ? (
        <div className="mb-4">
          <ErrorNote message="Listing complaints requires PH_INSPECTOR or SENIOR_PHI — logging a new one (e.g. phoned in) is open to any signed-in staff account." />
        </div>
      ) : null}

      <div className="mb-4 flex flex-wrap gap-2">
        <select className="field-input !w-52" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          {COMPLAINT_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s.replaceAll('_', ' ')}
            </option>
          ))}
        </select>
      </div>

      {listQ.error ? <ErrorNote message={listQ.error} /> : null}

      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No complaints match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Ref no.</th>
                <th className="table-head">Premises</th>
                <th className="table-head">District</th>
                <th className="table-head">Status</th>
                <th className="table-head">Filed</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((c) => (
                <tr key={c.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setDetailId(c.id)}>
                  <td className="table-cell font-mono text-xs">{c.refNo}</td>
                  <td className="table-cell font-medium">{c.premisesNameFreeText ?? c.applicationId ?? '—'}</td>
                  <td className="table-cell">{c.district ?? '—'}</td>
                  <td className="table-cell">
                    <StatusBadge status={c.status} />
                  </td>
                  <td className="table-cell text-xs">{formatDateTime(c.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showCreate ? (
        <CreateModal
          onClose={() => setShowCreate(false)}
          onDone={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}
      {detailId ? <DetailModal id={detailId} isPhaStaff={isPhaStaff} onClose={() => setDetailId(null)} onChanged={() => listQ.reload()} /> : null}
    </div>
  );
}

function CreateModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [premisesNameFreeText, setPremisesNameFreeText] = useState('');
  const [district, setDistrict] = useState('');
  const [complainantContact, setComplainantContact] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m25/complaints', {
        premisesNameFreeText: premisesNameFreeText || undefined,
        district: district || undefined,
        complainantContact: complainantContact || undefined,
        description,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to log complaint');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Log a sanitary complaint" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Premises name">
          <input className="field-input" value={premisesNameFreeText} onChange={(e) => setPremisesNameFreeText(e.target.value)} placeholder="e.g. Beau Vallon Beach Café" />
        </Field>
        <Field label="District">
          <input className="field-input" value={district} onChange={(e) => setDistrict(e.target.value)} />
        </Field>
        <Field label="Complainant contact (optional — leave blank to keep anonymous)">
          <input className="field-input" value={complainantContact} onChange={(e) => setComplainantContact(e.target.value)} placeholder="phone or email" />
        </Field>
        <Field label="Description">
          <textarea className="field-input" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || description.trim().length < 5 || (!premisesNameFreeText.trim() && true)}>
            {busy ? 'Logging…' : 'Log complaint'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function DetailModal({ id, isPhaStaff, onClose, onChanged }: { id: string; isPhaStaff: boolean; onClose: () => void; onChanged: () => void }) {
  const detailQ = useApiQuery<SanitaryComplaint>(`/api/v1/m25/complaints/${id}`);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const complaint = detailQ.data;

  const run = async (path: string) => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m25/complaints/${id}${path}`, { notes: notes || undefined });
      detailQ.reload();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Sanitary complaint" onClose={onClose}>
      {detailQ.loading || !complaint ? (
        <Spinner />
      ) : (
        <div className="space-y-4 text-sm">
          {error ? <ErrorNote message={error} /> : null}
          <div className="flex items-center justify-between">
            <span className="font-mono text-xs">{complaint.refNo}</span>
            <StatusBadge status={complaint.status} />
          </div>
          <dl className="space-y-1.5">
            <div className="flex justify-between">
              <dt className="text-slate-500">Premises</dt>
              <dd className="font-medium">{complaint.premisesNameFreeText ?? complaint.applicationId ?? '—'}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">District</dt>
              <dd className="font-medium">{complaint.district ?? '—'}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">Complainant</dt>
              <dd className="font-medium">{complaint.complainantSubject ? complaint.complainantContact ?? 'On file' : 'Anonymous'}</dd>
            </div>
          </dl>
          <p className="rounded-lg bg-slate-50 p-3 text-slate-700">{complaint.description}</p>
          {complaint.triageNotes ? <p className="text-xs text-slate-500">Triage notes: {complaint.triageNotes}</p> : null}
          {complaint.resolutionNotes ? <p className="text-xs text-slate-500">Resolution notes: {complaint.resolutionNotes}</p> : null}

          {isPhaStaff && complaint.status !== 'RESOLVED' && complaint.status !== 'DISMISSED' ? (
            <div className="space-y-2 border-t border-slate-100 pt-3">
              <Field label="Notes (optional)">
                <textarea className="field-input" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
              </Field>
              <div className="flex flex-wrap gap-1.5">
                {complaint.status === 'SUBMITTED' ? (
                  <button className="btn-primary !px-2.5 !py-1 text-xs" onClick={() => run('/triage')} disabled={busy}>
                    Triage
                  </button>
                ) : null}
                {complaint.status === 'TRIAGED' ? (
                  <button className="btn-primary !px-2.5 !py-1 text-xs" onClick={() => run('/trigger-inspection')} disabled={busy}>
                    Trigger inspection
                  </button>
                ) : null}
                <button className="btn-success !px-2.5 !py-1 text-xs" onClick={() => run('/resolve')} disabled={busy}>
                  Resolve
                </button>
                <button className="btn-danger !px-2.5 !py-1 text-xs" onClick={() => run('/dismiss')} disabled={busy}>
                  Dismiss
                </button>
              </div>
            </div>
          ) : null}
        </div>
      )}
    </Modal>
  );
}
